import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { listQuery } from '@/lib/pagination'
import type { EventListQuery } from '@lib/input/event-list.input'

import {
  eventDtoSchema,
  eventDeleteResultDtoSchema,
  eventDetailDtoSchema,
  eventListDtoSchema,
  eventPhaseDtoSchema,
  eventPlannerOptionListDtoSchema,
  type EventDto,
  type EventDeleteResultDto,
  type EventDetailDto,
  type EventListDto,
  type EventPhaseDto,
  type EventPlannerOptionListDto,
} from '@lib/dto/event.dto'
import {
  eventImportApplyResultDtoSchema,
  eventImportPreviewDtoSchema,
  type EventImportApplyResultDto,
  type EventImportPreviewDto,
} from '@lib/dto/event-import.dto'
import { furnitureGalleryDtoSchema, type FurnitureGalleryDto } from '@lib/dto/furniture-gallery.dto'
import { venueCalendarDtoSchema, type VenueCalendarDto } from '@lib/dto/venue-calendar.dto'
import type { VenueCalendarQuery } from '@lib/input/venue-calendar.input'
import type { TrackAvailabilityQuery } from '@lib/input/track-availability.input'
import { emailLogListDtoSchema, type EmailLogListDto } from '@lib/dto/email-log.dto'
import {
  trackAvailabilityListDtoSchema,
  type TrackAvailabilityListDto,
} from '@lib/dto/track-availability.dto'
import {
  venuePurgePreviewDtoSchema,
  venuePurgeResultDtoSchema,
  type VenuePurgePreviewDto,
  type VenuePurgeResultDto,
} from '@lib/dto/venue-purge.dto'
import type { PurgeVenueEventsInput } from '@lib/input/venue-purge.input'
import {
  eventEditorsDtoSchema,
  planImpactDtoSchema,
  type EventEditorsDto,
  type PlanImpactDto,
} from '@lib/dto/plan-impact.dto'
import type { RoomChangeImpactInput } from '@lib/input/room-impact.input'
import type { StockChangeImpactInput } from '@lib/input/stock-impact.input'
import { userDtoSchema, userListDtoSchema, type UserDto, type UserListDto } from '@lib/dto/user.dto'
import type { UpdateClientPhoneInput } from '@lib/input/user.input'
import { z } from 'zod'
import type {
  AddBroughtInInput,
  AddEventClientInput,
  CreateEventInput,
  ConfirmedPlansChoice,
  CreatePhaseInput,
  DeleteEventQuery,
  LoseEventInput,
  ReopenEventInput,
  RequestedDatesInput,
  HoldInput,
  SavePhaseInput,
  UpdateBroughtInInput,
  UpdateEventInput,
  SetGuestCountInput,
  UpdatePhaseInput,
} from '@lib/input/event.input'
import type { ApplyEventImportInput, PreviewEventImportInput } from '@lib/input/event-import.input'
import type { AssignEventVendorInput, VendorSearchQuery } from '@lib/input/vendor.input'
import { venueVendorChoiceListDtoSchema, type VenueVendorChoiceListDto } from '@lib/dto/vendor.dto'
import { clientToPlannerDtoSchema, type ClientToPlannerDto } from '@lib/dto/planner-conversion.dto'
import type { ClientToPlannerInput } from '@lib/input/planner-conversion.input'
import {
  eventDocumentDtoSchema,
  eventDocumentListDtoSchema,
  type EventDocumentDto,
  type EventDocumentListDto,
} from '@lib/dto/event-document.dto'
import type { CreateEventDocumentInput, UpdateEventDocumentInput } from '@lib/input/event-document.input'

/** Service for the event REST endpoints (scoped per caller by the API). */
class EventService {
  async list(query: EventListQuery = {}, page?: number): Promise<EventListDto> {
    try {
      return await api.get(`/events${listQuery({ ...query }, page)}`, eventListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list events', {}, error)
      throw error
    }
  }

  async get(id: string): Promise<EventDetailDto> {
    try {
      return await api.get(`/events/${id}`, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load event', { id }, error)
      throw error
    }
  }

  async create(input: CreateEventInput): Promise<EventDto> {
    try {
      return await api.post('/events', input, eventDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create event', {}, error)
      throw error
    }
  }

  /** Edit an event's own fields (name, date, time window, guest count). */
  async update(id: string, input: UpdateEventInput): Promise<EventDetailDto> {
    try {
      return await api.put(`/events/${id}`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update event', { id }, error)
      throw error
    }
  }

  /** Set the guest count: open to the couple and their planner as well as staff. */
  async setGuestCount(id: string, input: SetGuestCountInput): Promise<EventDetailDto> {
    try {
      return await api.put(`/events/${id}/guest-count`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set the guest count', { id }, error)
      throw error
    }
  }

  /** Mark an inquiry lost, with a reason. */
  async lose(id: string, input: LoseEventInput): Promise<EventDetailDto> {
    try {
      return await api.post(`/events/${id}/loss`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to mark inquiry lost', { id }, error)
      throw error
    }
  }

  /** Reopen a lost inquiry. */
  async reopen(id: string, input: ReopenEventInput): Promise<EventDetailDto> {
    try {
      return await api.delWith(`/events/${id}/loss`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reopen inquiry', { id }, error)
      throw error
    }
  }

  /** Save the dates an inquiry's couple asked about, whole. */
  async setRequestedDates(id: string, input: RequestedDatesInput): Promise<EventDetailDto> {
    try {
      return await api.put(`/events/${id}/requested-dates`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save requested dates', { id }, error)
      throw error
    }
  }

  /** Hold a date for an inquiry until a chosen day, replacing its hold. */
  async placeHold(id: string, input: HoldInput): Promise<EventDetailDto> {
    try {
      return await api.put(`/events/${id}/hold`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to hold date', { id }, error)
      throw error
    }
  }

  /** Release an inquiry's hold. */
  async releaseHold(id: string): Promise<EventDetailDto> {
    try {
      return await api.delFor(`/events/${id}/hold`, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to release hold', { id }, error)
      throw error
    }
  }

  /**
   * Which of a venue's tracks are free for a window, and who holds the rest.
   *
   * Only the parameters that were asked about are sent: the API reads a missing
   * `date` as "no window to judge" and answers `unknown` for every lane, which
   * is not the same as free.
   */
  async trackAvailability(query: TrackAvailabilityQuery): Promise<TrackAvailabilityListDto> {
    const params = new URLSearchParams({ venueId: query.venueId })
    // The clocks are reachable only inside the dated branch, so this cannot
    // send a window without the day it is on — the shape the API refuses.
    if ('date' in query) {
      params.set('date', query.date)
      if (query.startsAt !== undefined) params.set('startsAt', query.startsAt)
      if (query.endsAt !== undefined) params.set('endsAt', query.endsAt)
    }
    if (query.excludeEventId !== undefined) params.set('excludeEventId', query.excludeEventId)
    try {
      return await api.get(`/events/track-availability?${params.toString()}`, trackAvailabilityListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load track availability', { ...query }, error)
      throw error
    }
  }

  /** Which event plans a change to a venue room would reach, before it is made. */
  async roomImpact(input: RoomChangeImpactInput): Promise<PlanImpactDto> {
    try {
      return await api.post('/events/room-impact', input, planImpactDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to check what a room change would reach', { versionId: input.versionId }, error)
      throw error
    }
  }

  /** Which plans a change to a venue's stock would leave short, before it is made. */
  async stockImpact(input: StockChangeImpactInput): Promise<PlanImpactDto> {
    try {
      return await api.post('/events/stock-impact', input, planImpactDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to check what a stock change would reach', { venueId: input.venueId }, error)
      throw error
    }
  }

  /** Every message sent to this event's client contacts, newest first. */
  async emailLog(eventId: string): Promise<EmailLogListDto> {
    try {
      return await api.get(`/events/${eventId}/emails`, emailLogListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the event email log', { eventId }, error)
      throw error
    }
  }

  /** What one venue has on between two dates, for the dashboard calendar. */
  async calendar(query: VenueCalendarQuery): Promise<VenueCalendarDto> {
    const params = new URLSearchParams({ venueId: query.venueId, from: query.from, to: query.to })
    try {
      return await api.get(`/events/calendar?${params.toString()}`, venueCalendarDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue calendar', { ...query }, error)
      throw error
    }
  }

  /**
   * Permanently delete an event (admin-only). `clients: 'remove'` also retires the
   * client accounts this event was the last of; the result says what happened to
   * them, since a client who is on another event is kept either way.
   */
  async delete(id: string, clients: DeleteEventQuery['clients'] = 'keep'): Promise<EventDeleteResultDto> {
    try {
      return await api.delFor(`/events/${id}?clients=${clients}`, eventDeleteResultDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to delete event', { id }, error)
      throw error
    }
  }

  /** What clearing a venue's events would remove — read before the confirmation. */
  async venuePurgePreview(venueId: string): Promise<VenuePurgePreviewDto> {
    try {
      return await api.get(`/events/venue/${venueId}/purge`, venuePurgePreviewDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to count what clearing a venue would remove', { venueId }, error)
      throw error
    }
  }

  /**
   * Delete every event at one venue (organization admins only). `clients: 'remove'`
   * also retires the contacts nothing outside this venue still names; the result
   * says what happened to them.
   */
  async purgeVenue(venueId: string, input: PurgeVenueEventsInput): Promise<VenuePurgeResultDto> {
    try {
      return await api.post(`/events/venue/${venueId}/purge`, input, venuePurgeResultDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to clear a venue', { venueId }, error)
      throw error
    }
  }

  async savePhase(eventId: string, phaseId: string, input: SavePhaseInput): Promise<EventPhaseDto> {
    try {
      return await api.put(`/events/${eventId}/phases/${phaseId}`, input, eventPhaseDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save event phase', { eventId, phaseId }, error)
      throw error
    }
  }

  async createPhase(eventId: string, input: CreatePhaseInput): Promise<EventDetailDto> {
    try {
      return await api.post(`/events/${eventId}/phases`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create phase', { eventId }, error)
      throw error
    }
  }

  async updatePhase(eventId: string, phaseId: string, input: UpdatePhaseInput): Promise<EventDetailDto> {
    try {
      return await api.put(`/events/${eventId}/phases/${phaseId}/meta`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update phase', { eventId, phaseId }, error)
      throw error
    }
  }

  async deletePhase(eventId: string, phaseId: string): Promise<void> {
    try {
      await api.del(`/events/${eventId}/phases/${phaseId}`)
    } catch (error) {
      logRequestFailure('Failed to delete phase', { eventId, phaseId }, error)
      throw error
    }
  }

  async addBroughtIn(eventId: string, input: AddBroughtInInput): Promise<EventDetailDto> {
    try {
      return await api.post(`/events/${eventId}/brought-in`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to add brought-in item', { eventId }, error)
      throw error
    }
  }

  async updateBroughtIn(eventId: string, itemId: string, input: UpdateBroughtInInput): Promise<EventDetailDto> {
    try {
      return await api.patch(`/events/${eventId}/brought-in/${itemId}`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update brought-in item', { eventId, itemId }, error)
      throw error
    }
  }

  /**
   * Take a brought-in piece off the event. Open plans keep its pieces, marked,
   * until someone takes them off; `confirmedPlans` says whether confirmed and
   * finished plans keep it too.
   */
  async removeBroughtIn(eventId: string, itemId: string, confirmedPlans: ConfirmedPlansChoice = 'keep'): Promise<void> {
    try {
      await api.del(`/events/${eventId}/brought-in/${itemId}?confirmedPlans=${confirmedPlans}`)
    } catch (error) {
      logRequestFailure('Failed to remove brought-in item', { eventId, itemId }, error)
      throw error
    }
  }

  /** Who else has each of the event's plans open right now. */
  async editors(eventId: string): Promise<EventEditorsDto> {
    try {
      return await api.get(`/events/${eventId}/editors`, eventEditorsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to check who has the plans open', { eventId }, error)
      throw error
    }
  }

  /** Which plans a brought-in or rented piece is on, before it is taken off. */
  async furnitureRemovalImpact(eventId: string, itemId: string): Promise<PlanImpactDto> {
    try {
      return await api.get(`/events/${eventId}/furniture/${itemId}/removal-impact`, planImpactDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to check which plans a piece is on', { eventId, itemId }, error)
      throw error
    }
  }

  /** The planner's furniture gallery — venue-owned (for browsing; auto in the
   * sidebar) + vendor-available pieces to curate for the event. */
  async furnitureGallery(eventId: string): Promise<FurnitureGalleryDto> {
    try {
      return await api.get(`/events/${eventId}/furniture-gallery`, furnitureGalleryDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load furniture gallery', { eventId }, error)
      throw error
    }
  }

  /** Select how many of a vendor gallery piece this event rents (0 removes it).
   * Once added it's available in every phase. Capped by the vendor's stock. */
  async setRental(
    eventId: string,
    itemId: string,
    quantity: number,
    confirmedPlans?: ConfirmedPlansChoice,
  ): Promise<EventDetailDto> {
    try {
      return await api.put(
        `/events/${eventId}/rentals/${itemId}`,
        { quantity, ...(confirmedPlans !== undefined ? { confirmedPlans } : {}) },
        eventDetailDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to set rental', { eventId, itemId }, error)
      throw error
    }
  }

  async lockPhase(eventId: string, phaseId: string): Promise<EventPhaseDto> {
    try {
      return await api.post(`/events/${eventId}/phases/${phaseId}/lock`, {}, eventPhaseDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to lock phase', { eventId, phaseId }, error)
      throw error
    }
  }

  async unlockPhase(eventId: string, phaseId: string): Promise<EventPhaseDto> {
    try {
      return await api.post(`/events/${eventId}/phases/${phaseId}/unlock`, {}, eventPhaseDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to unlock phase', { eventId, phaseId }, error)
      throw error
    }
  }

  // ---- legacy SVG import (admin) ----

  /** Dry-run a legacy layout SVG import for one space of an event. */
  async previewImport(eventId: string, input: PreviewEventImportInput): Promise<EventImportPreviewDto> {
    try {
      return await api.post(`/events/${eventId}/import/preview`, input, eventImportPreviewDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to preview event import', { eventId }, error)
      throw error
    }
  }

  /** Apply a legacy layout SVG import (catalog + inventory + phase plan). */
  async applyImport(eventId: string, input: ApplyEventImportInput): Promise<EventImportApplyResultDto> {
    try {
      return await api.post(`/events/${eventId}/import/apply`, input, eventImportApplyResultDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to apply event import', { eventId }, error)
      throw error
    }
  }

  /** The companies planning the caller's events, for the list's Planner filter. */
  async planners(): Promise<EventPlannerOptionListDto> {
    try {
      return await api.get('/events/planners', eventPlannerOptionListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list event planners', {}, error)
      throw error
    }
  }

  // ---- the event's planner (admin) ----

  /** What marking one of the event's clients as a planner would do (decision 32). */
  async previewPlannerConversion(eventId: string, clientId: string, input: ClientToPlannerInput): Promise<ClientToPlannerDto> {
    // `toString()`, not `size`: Safari before 17 has no `size`, and the name typed would be dropped.
    const params = new URLSearchParams(input.companyName ? { companyName: input.companyName } : {}).toString()
    const query = params === '' ? '' : `?${params}`
    try {
      return await api.get(`/events/${eventId}/clients/${clientId}/planner-conversion${query}`, clientToPlannerDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to preview planner conversion', { eventId, clientId }, error)
      throw error
    }
  }

  /** Mark one of the event's clients as a planner, everywhere they are a client. */
  async convertClientToPlanner(eventId: string, clientId: string, input: ClientToPlannerInput): Promise<ClientToPlannerDto> {
    try {
      return await api.post(`/events/${eventId}/clients/${clientId}/planner-conversion`, input, clientToPlannerDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to convert client to planner', { eventId, clientId }, error)
      throw error
    }
  }

  /** Make a company the event's planner — one from search, or a new one and its owner. */
  async assignVendor(eventId: string, input: AssignEventVendorInput): Promise<EventDetailDto> {
    try {
      return await api.post(`/events/${eventId}/vendors`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to assign event vendor', { eventId }, error)
      throw error
    }
  }

  /** Take a company off the event; its people lose it at once. */
  async removeVendor(eventId: string, assignmentId: string): Promise<EventDetailDto> {
    try {
      return await api.delFor(`/events/${eventId}/vendors/${assignmentId}`, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to remove event vendor', { eventId, assignmentId }, error)
      throw error
    }
  }

  /** The venue's vendors, for the event's planner to pick from. */
  async venueVendorChoices(eventId: string, query: VendorSearchQuery): Promise<VenueVendorChoiceListDto> {
    const params = new URLSearchParams({ q: query.q })
    if (query.service !== undefined) params.set('service', query.service)
    try {
      return await api.get(`/events/${eventId}/vendors/choices?${params.toString()}`, venueVendorChoiceListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list the venue’s vendors', { eventId }, error)
      throw error
    }
  }

  /** Send the company contact's invitation again, while they have not accepted. */
  async inviteVendorContact(eventId: string, assignmentId: string): Promise<void> {
    try {
      await api.post(`/events/${eventId}/vendors/${assignmentId}/invite`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to invite event vendor contact', { eventId, assignmentId }, error)
      throw error
    }
  }

  // ---- client contacts (admin) ----

  /** Search the tenant's client contacts (for the add picker). */
  async searchClients(q: string): Promise<UserListDto> {
    try {
      return await api.get(`/clients?q=${encodeURIComponent(q)}`, userListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to search clients', {}, error)
      throw error
    }
  }

  /** Add a client contact to an event (existing by id, or new by name+email). */
  async addClient(eventId: string, input: AddEventClientInput): Promise<EventDetailDto> {
    try {
      return await api.post(`/events/${eventId}/clients`, input, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to add event client', { eventId }, error)
      throw error
    }
  }

  async removeClient(eventId: string, clientId: string): Promise<void> {
    try {
      await api.del(`/events/${eventId}/clients/${clientId}`)
    } catch (error) {
      logRequestFailure('Failed to remove event client', { eventId, clientId }, error)
      throw error
    }
  }

  async inviteClient(clientId: string): Promise<void> {
    try {
      await api.post(`/clients/${clientId}/invite`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to invite client', { clientId }, error)
      throw error
    }
  }

  async deletePendingClient(clientId: string): Promise<void> {
    try {
      await api.del(`/clients/${clientId}`)
    } catch (error) {
      logRequestFailure('Failed to delete invited client', { clientId }, error)
      throw error
    }
  }

  async deactivateClient(clientId: string): Promise<UserDto> {
    try {
      return await api.post(`/clients/${clientId}/deactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to deactivate client', { clientId }, error)
      throw error
    }
  }

  async setClientPhone(clientId: string, input: UpdateClientPhoneInput): Promise<UserDto> {
    try {
      return await api.put(`/clients/${clientId}/phone`, input, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save a client’s phone', { clientId }, error)
      throw error
    }
  }

  async reactivateClient(clientId: string): Promise<UserDto> {
    try {
      return await api.post(`/clients/${clientId}/reactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reactivate client', { clientId }, error)
      throw error
    }
  }

  // ---- documents ----------------------------------------------------------

  async listDocuments(eventId: string): Promise<EventDocumentListDto> {
    try {
      return await api.get(`/events/${eventId}/documents`, eventDocumentListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list event documents', { eventId }, error)
      throw error
    }
  }

  async createDocument(eventId: string, input: CreateEventDocumentInput): Promise<EventDocumentDto> {
    try {
      return await api.post(`/events/${eventId}/documents`, input, eventDocumentDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create event document', { eventId }, error)
      throw error
    }
  }

  async updateDocument(
    eventId: string,
    documentId: string,
    input: UpdateEventDocumentInput,
  ): Promise<EventDocumentDto> {
    try {
      return await api.put(`/events/${eventId}/documents/${documentId}`, input, eventDocumentDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update event document', { eventId, documentId }, error)
      throw error
    }
  }

  async deleteDocument(eventId: string, documentId: string): Promise<void> {
    try {
      await api.del(`/events/${eventId}/documents/${documentId}`)
    } catch (error) {
      logRequestFailure('Failed to delete event document', { eventId, documentId }, error)
      throw error
    }
  }
}

export const eventService = new EventService()
